<template>
  <div class="sgf-history-form">
    <div class="sgf-history-header">
      <div class="modal-title">Registro de Histórico no SGF</div>
    </div>

    <validation-observer class="form-holder nice-Vscroll" ref="historyValidation">
      <div v-if="hasNoContracts" class="info-box">
        <div class="info-title">Essa oportunidade não possui nenhum contrato registrado.</div>

        <div>Informe o ID do contrato no SGF para registrar esta ação também no histórico.</div>
      </div>

      <validation-provider
        v-if="hasNoContracts"
        #default="{ errors }"
        name="ID do Contrato"
        rules="required|integer"
        vid="contractId"
      >
        <small class="v-err text-danger mb-1">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="contractId" for="sgf_contract">ID do Contrato</label>

          <input
            id="sgf_contract"
            class="sgf-form-input"
            type="text"
            inputmode="numeric"
            placeholder="ID do contrato no SGF"
            v-model.trim="contractId"
          />
        </div>
      </validation-provider>

      <validation-provider
        v-else-if="hasMultipleContracts"
        #default="{ errors }"
        name="ID do Contrato"
        rules="required"
        vid="contractId"
      >
        <small class="v-err text-danger mb-1">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="contractId" for="sgf_contract">ID do Contrato</label>

          <v-select
            :options="contracts"
            :clearable="false"
            id="sgf_contract"
            class="labeled-vselect"
            placeholder="Selecione o ID do contrato"
            v-model="contractId"
          />
        </div>
      </validation-provider>

      <div v-else class="field-label">
        <label for="sgf_contract">ID do Contrato</label>

        <input :value="contractId" id="sgf_contract" class="sgf-form-input" type="text" disabled />
      </div>

      <validation-provider #default="{ errors }" name="Tipo de Histórico" rules="required" vid="typeId">
        <small class="v-err text-danger mb-1">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="typeId" for="sgf_history_type">Tipo de Histórico</label>

          <v-select
            :options="historyTypes"
            :reduce="(option) => option.id"
            :clearable="false"
            id="sgf_history_type"
            label="name"
            class="labeled-vselect"
            placeholder="Tipo de Histórico"
            v-model="typeId"
          />
        </div>
      </validation-provider>

      <validation-provider #default="{ errors }" name="Descrição" rules="required" vid="description">
        <small class="v-err text-danger mb-1">{{ errors[0] }}</small>

        <div class="field-label">
          <label v-show="description" for="sgf_description">Descrição do Histórico</label>

          <textarea
            :disabled="hasLastTaskRequest"
            id="sgf_description"
            class="observation"
            rows="6"
            placeholder="Descrição do Histórico"
            v-model="description"
          />
        </div>
      </validation-provider>
    </validation-observer>

    <div class="bottom-menu">
      <b-button @click="skip()" :disabled="hasSaveRequest" variant="light">
        {{ hasNoContracts ? "Continuar sem contrato" : "Pular registro" }}
      </b-button>

      <b-button @click="submit()" :disabled="hasSaveRequest || hasLastTaskRequest" variant="primary">
        <b-spinner v-if="hasSaveRequest" small />

        <div v-else>Registrar</div>
      </b-button>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import "@validations";

const HISTORY_TITLE = "CRM | Registro de Histórico";

const HISTORY_TYPES = [
  { id: 24, name: "Aluno avisou que irá faltar" },
  { id: 31, name: "Pós Venda - Pedagógico" },
  { id: 68, name: "Intenção de Cancelar" },
  { id: 94, name: "Tentativa de contato - Falta" },
  { id: 146, name: "Contato Falta On-line" },
  { id: 151, name: "Atendimento Financeiro Online" },
  { id: 152, name: "Atendimento Pedagógico - Online" },
  { id: 166, name: "WhatsApp - Início de Aula" },
];

const HISTORY_TYPE_BY_PRODUCT = {
  5144: 24,
  5146: 151,
  5148: 152,
  5149: 68,
  6443: 31,
  6434: 146,
  6431: 146,
  6433: 146,
  6432: 146,
  6430: 146,
  6676: 146,
  6679: 166,
};

export default {
  components: {
    BButton,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },

  props: {
    opportunity: {
      type: Object,
      required: true,
    },

    hasSaveRequest: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      historyTypes: HISTORY_TYPES,
      contractId: null,
      typeId: null,
      description: "",
      hasLastTaskRequest: false,
    };
  },

  computed: {
    user() {
      return this.$store.getters["app/getUserData"];
    },

    contracts() {
      return this.opportunity.erp_contract_ids || [];
    },

    hasNoContracts() {
      return !this.contracts.length;
    },

    hasMultipleContracts() {
      return this.contracts.length > 1;
    },
  },

  created() {
    if (this.contracts.length === 1) this.contractId = this.contracts[0];

    this.typeId = this.getSuggestedTypeId();

    this.fetchLastTaskDescription();
  },

  methods: {
    getSuggestedTypeId() {
      const suggestedProduct = (this.opportunity.products || []).find(
        (product) => HISTORY_TYPE_BY_PRODUCT[product.id],
      );

      return suggestedProduct ? HISTORY_TYPE_BY_PRODUCT[suggestedProduct.id] : null;
    },

    buildPayload() {
      const payload = {
        contract_id: Number(this.contractId),
        description: this.description,
        title: HISTORY_TITLE,
        type_id: this.typeId,
      };

      if (this.user?.remote_id) payload.user_id = this.user.remote_id;

      return payload;
    },

    skip() {
      this.$emit("skip");
    },

    submit() {
      this.$refs.historyValidation.validate().then((success) => {
        if (!success) return;

        this.$emit("confirm", this.buildPayload());
      });
    },

    fetchLastTaskDescription() {
      this.hasLastTaskRequest = true;

      const params = [
        { query: "parent_id", param: this.opportunity.id },
        { query: "parent_type", param: "Opportunity" },
        { query: "sort", param: "-created_at" },
        { query: "limit", param: 1 },
      ];

      this.$store
        .dispatch("calendarStore/fetchTasks", this.$service.formatQueryParam(params))
        .then((response) => {
          const lastTask = response.data?.[0];

          if (!lastTask || this.description) return;

          this.description = lastTask.ended_description || lastTask.description || "";
        })
        .finally(() => {
          this.hasLastTaskRequest = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.sgf-history-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  height: 100dvh;
  width: 100%;
  overflow: hidden;

  .sgf-history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 15px 10px;
    border-bottom: 1px solid #ececec;
    color: #31333f;

    .modal-title {
      font-size: 15px;
      font-weight: 600;
    }
  }

  .form-holder {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 1.5rem;
    gap: 15px;
    overflow-x: hidden;
    overflow-y: auto;

    .info-box {
      display: flex;
      flex-direction: column;
      padding: 10px 1rem;
      gap: 5px;
      border-radius: 5px;
      border: 1px solid #c6c6c6;
      background: #ffffff;
      color: #6e6b7b;

      .info-title {
        font-weight: 600;
        color: #31333f;
      }
    }

    .field-label {
      position: relative;

      label {
        font-size: 12px;
        font-weight: 400;
        position: absolute;
        top: -11px;
        left: 15px;
        z-index: 100;
        padding: 3px;
        border-radius: 5px;
        background: #ffffff;
        color: #b1b1b1;
      }

      :deep(.labeled-vselect) {
        .vs__dropdown-toggle {
          padding: 5px 5px 9px 5px;
        }

        .vs__selected {
          color: #6e6b7b;
        }
      }

      .sgf-form-input {
        height: 42px;
        width: 100%;
        padding: 10px;
        border-radius: 5px;
        border: 1px solid #c6c6c6;
        outline: none;
        color: #6e6b7b;

        &:disabled {
          background: #ffffff;
        }
      }

      .observation {
        width: 100%;
        padding: 10px;
        border-radius: 5px;
        border: 1px solid #c6c6c6;
        outline: none;
        color: #6e6b7b;
      }
    }
  }

  .bottom-menu {
    display: flex;
    justify-content: space-around;
    align-items: center;
    font-weight: bold;
    width: 100%;
    padding: 10px;
    margin-top: auto;
    border-top: 1px solid #ececec;
    color: #000000;
    background: #ffffff;
  }
}
</style>
